import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { units as unitsApi, type CouncilArea, type Role, type UnitSummary } from './api';
import { useAuth } from './auth';

const LAST_UNIT_KEY = 'scout.lastUnitId';

function readLastUnit(): string | null {
  try {
    return localStorage.getItem(LAST_UNIT_KEY);
  } catch {
    return null;
  }
}
function writeLastUnit(id: string | null) {
  try {
    if (id) localStorage.setItem(LAST_UNIT_KEY, id);
    else localStorage.removeItem(LAST_UNIT_KEY);
  } catch {
    /* private mode / storage disabled — fine, we just won't remember */
  }
}

interface UnitsState {
  units: UnitSummary[];
  loading: boolean;
  currentUnit: UnitSummary | null;
  setCurrentUnitId: (id: string) => void;
  reload: () => Promise<void>;
  /** Roles the user holds in the current unit. */
  roles: Role[];
  hasRole: (role: Role) => boolean;
  /** SCO-18 — true if a COUNCIL member is granted view of `area` in the current unit. */
  councilCanView: (area: CouncilArea) => boolean;
  /** SCO-19 — true if a COUNCIL member has the messaging/announcements grant here. */
  councilCanMessage: boolean;
}

const UnitsContext = createContext<UnitsState | undefined>(undefined);

export function UnitsProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const [units, setUnits] = useState<UnitSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUnitId, setCurrentUnitIdState] = useState<string | null>(readLastUnit());

  const reload = useCallback(async () => {
    if (!me) {
      setUnits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = await unitsApi.mine();
      setUnits(next);
      setCurrentUnitIdState((prev) => {
        if (prev && next.some((u) => u.id === prev)) return prev;
        return next[0]?.id ?? null;
      });
    } finally {
      setLoading(false);
    }
  }, [me]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const setCurrentUnitId = useCallback((id: string) => {
    setCurrentUnitIdState(id);
    writeLastUnit(id);
  }, []);

  const currentUnit = useMemo(
    () => units.find((u) => u.id === currentUnitId) ?? null,
    [units, currentUnitId],
  );

  const roles = useMemo<Role[]>(() => currentUnit?.roles ?? [], [currentUnit]);
  const hasRole = useCallback((role: Role) => roles.includes(role), [roles]);
  const councilAreas = useMemo(() => currentUnit?.councilViewAreas ?? [], [currentUnit]);
  const councilCanView = useCallback(
    (area: CouncilArea) => councilAreas.includes(area),
    [councilAreas],
  );
  const councilCanMessage = currentUnit?.councilCanMessage ?? false;

  const value = useMemo<UnitsState>(
    () => ({
      units,
      loading,
      currentUnit,
      setCurrentUnitId,
      reload,
      roles,
      hasRole,
      councilCanView,
      councilCanMessage,
    }),
    [units, loading, currentUnit, setCurrentUnitId, reload, roles, hasRole, councilCanView, councilCanMessage],
  );

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
}

export function useUnits(): UnitsState {
  const ctx = useContext(UnitsContext);
  if (!ctx) throw new Error('useUnits must be used within UnitsProvider');
  return ctx;
}
